import type { CrawlPage } from '../hooks/useCrawls'
import { decodeHtmlEntities } from '@/client/lib/decode'

function truncate(text: string, maxChars: number): string {
  if (text.length <= maxChars) return text
  return text.slice(0, maxChars).trimEnd() + '...'
}

function extractDomain(url: string): string {
  try {
    return new URL(url).hostname
  } catch {
    return url
  }
}

function OgPreview({ page }: { page: CrawlPage }) {
  const title = decodeHtmlEntities(page.og_title || page.title || '')
  const description = decodeHtmlEntities(page.og_description || page.meta_description || '')
  const image = page.og_image
  const domain = extractDomain(page.url)

  const hasOg = page.og_title || page.og_description || page.og_image

  if (!hasOg) {
    return (
      <div className="text-xs text-muted-foreground/50 italic py-4 text-center border rounded-lg">
        No Open Graph tags found — social shares will use page title and description.
      </div>
    )
  }

  return (
    <div className="rounded-lg border overflow-hidden max-w-[500px] bg-[#f0f2f5] dark:bg-zinc-900">
      {/* Image */}
      {image ? (
        <div className="aspect-[1.91/1] bg-muted relative overflow-hidden">
          <img
            src={image}
            alt="OG preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
      ) : (
        <div className="aspect-[1.91/1] bg-muted flex items-center justify-center">
          <span className="text-xs text-muted-foreground">No og:image set</span>
        </div>
      )}
      {/* Content */}
      <div className="p-3 space-y-0.5">
        <p className="text-[11px] uppercase text-muted-foreground tracking-wide">{domain}</p>
        <p className="text-sm font-semibold leading-tight text-foreground">
          {title ? truncate(title, 65) : <span className="text-muted-foreground italic">No og:title</span>}
        </p>
        {description && (
          <p className="text-xs text-muted-foreground leading-snug">{truncate(description, 110)}</p>
        )}
      </div>
    </div>
  )
}

function TwitterPreview({ page }: { page: CrawlPage }) {
  const cardType = page.twitter_card || 'summary'
  const title = decodeHtmlEntities(page.twitter_title || page.og_title || page.title || '')
  const description = decodeHtmlEntities(page.twitter_description || page.og_description || page.meta_description || '')
  const image = page.og_image // Twitter falls back to og:image
  const domain = extractDomain(page.url)

  if (!page.twitter_card && !page.og_title) {
    return null // No Twitter-specific or OG data, skip
  }

  const isLargeImage = cardType === 'summary_large_image'

  return (
    <div className="rounded-xl border overflow-hidden max-w-[500px] bg-white dark:bg-zinc-900">
      {isLargeImage && image && (
        <div className="aspect-[2/1] bg-muted relative overflow-hidden">
          <img
            src={image}
            alt="Twitter preview"
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
      )}
      <div className={`${isLargeImage ? 'p-3' : 'flex gap-3 p-3'}`}>
        {!isLargeImage && image && (
          <div className="h-[125px] w-[125px] flex-shrink-0 rounded overflow-hidden bg-muted">
            <img
              src={image}
              alt="Twitter preview"
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none'
              }}
            />
          </div>
        )}
        <div className="min-w-0 space-y-0.5">
          <p className="text-sm font-semibold leading-tight text-foreground truncate">
            {title ? truncate(title, 70) : <span className="text-muted-foreground italic">No title</span>}
          </p>
          {description && (
            <p className="text-xs text-muted-foreground leading-snug line-clamp-2">{truncate(description, 120)}</p>
          )}
          <p className="text-xs text-muted-foreground">{domain}</p>
        </div>
      </div>
    </div>
  )
}

export function SocialPreview({ page }: { page: CrawlPage }) {
  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">Facebook / LinkedIn Preview</p>
        <OgPreview page={page} />
      </div>
      <div>
        <p className="text-xs font-medium text-muted-foreground mb-2">
          Twitter/X Preview
          {page.twitter_card && (
            <span className="ml-1.5 font-normal">({page.twitter_card})</span>
          )}
        </p>
        <TwitterPreview page={page} />
      </div>
    </div>
  )
}
